import { gql, useQuery } from '@apollo/client';
import IconArrowRight from '@apollo/icons/small/IconArrowRight.svg';
import {
  Button,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import { capitalize } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { insightsRouteConfig } from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useOperationInsightsFilter } from 'src/hooks/useInsightsFilter';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { date } from 'src/lib/date/date';
import { format } from 'src/lib/format';
import {
  ErrorInsightsBreakdownItemFragment,
  ErrorInsightsBreakdownQuery,
  ErrorInsightsListGroupByColumn,
} from 'src/lib/graphqlTypes/types';
import { notEmpty } from 'src/lib/ts/notEmpty';

import { buildErrorFiltersForListQuery } from './buildErrorFilters';
import { ErrorBreakdownLoading } from './ErrorBreakdownLoading';
import {
  getRouteParamForBreakdownOption,
  getRouteParamForGroupOption,
} from './getRouteParam';
import { BreakdownOption, GroupOption } from './GroupByButton';
import { GroupIcon, GroupRightIcon } from './GroupIcon';
import { SampleMessage, SampleMessageHeader } from './SampleMessage';
import { TraceRefBadge, errorTraceRefFragment } from './TraceRefBadge';
import { useDisplayNameTranslator } from './useDisplayNameTranslator';

const errorInsightsBreakdownItemFragment = gql`
  fragment ErrorInsightsBreakdownItemFragment on ErrorInsightsListItem {
    clientName
    clientVersion
    code
    errorCount
    operationCount @include(if: $isOverview)
    operationId
    operationName
    path
    service
    traceRefs(limit: $traceRefsLimit) {
      totalCount
      items {
        ...ErrorTraceRefFragment
      }
    }
  }
`;

export const errorInsightsBreakdownQuery = gql`
  query ErrorInsightsBreakdownQuery(
    $graphId: ID!
    $variant: String!
    $from: Timestamp!
    $to: Timestamp!
    $first: Int!
    $filter: ErrorInsightsListFilterInput
    $groupBy: [ErrorInsightsListGroupByColumn!]
    $isOverview: Boolean!
    $traceRefsLimit: Int!
  ) {
    graph(id: $graphId) {
      id
      variant(name: $variant) {
        id
        errorInsightsList(
          first: $first
          from: $from
          to: $to
          filter: $filter
          groupBy: $groupBy
        ) {
          edges {
            node {
              ...ErrorInsightsBreakdownItemFragment
            }
            cursor
          }
          pageInfo {
            endCursor
            startCursor
          }
          totalCount
        }
      }
    }
  }
  ${errorInsightsBreakdownItemFragment}
  ${errorTraceRefFragment}
`;
export interface ErrorBreakdownItemData {
  dimensionValue: string;
  translatedDimensionValue: string;
  errorCount: number;
  traceRefs: ErrorInsightsBreakdownItemFragment['traceRefs'];
}

interface Props {
  dimensionName: string;
  groupColor: string;
  data: ErrorBreakdownItemData[];
  breakdownByDimension: BreakdownOption;
}

export function ErrorBreakdown({
  dimensionName,
  data,
  breakdownByDimension,
}: Props) {
  return (
    <TableContainer>
      <Table layout="fixed">
        <Thead>
          <Tr>
            <Th className="w-[30%]">{dimensionName}</Th>
            <Th className="w-[25%]">
              <SampleMessageHeader />
            </Th>
            <Th isNumeric className="w-[10%]">
              Errors
            </Th>
            <Th className="w-[35%]">Traces</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((d) => (
            <Tr key={d.dimensionValue}>
              <Td>
                <div className="flex items-center">
                  <Text
                    size="sm"
                    variant="code"
                    className="mr-1 overflow-hidden truncate rounded border border-primary bg-secondary px-2 py-0.5"
                  >
                    <GroupIcon
                      groupByDimension={breakdownByDimension}
                      dimensionValue={d.dimensionValue}
                    />
                    <span
                      className="ml-2 align-middle leading-tight"
                      title={d.translatedDimensionValue}
                    >
                      {d.translatedDimensionValue}
                    </span>
                  </Text>
                  <GroupRightIcon
                    groupByDimension={breakdownByDimension}
                    dimensionValue={d.dimensionValue}
                  />
                </div>
              </Td>
              <Td>
                <SampleMessage traceRefs={d.traceRefs} />
              </Td>
              <Td isNumeric>
                <span className="font-mono">
                  {format.number(d.errorCount, '0[.]0a')}
                </span>
              </Td>
              <Td>
                {(d.traceRefs?.items ?? []).length > 0 && (
                  <span className="flex flex-wrap gap-2">
                    {d.traceRefs?.items?.map((traceRef) => (
                      <TraceRefBadge
                        key={traceRef.traceId}
                        traceRef={traceRef}
                      />
                    ))}
                  </span>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

export interface ErrorBreakdownItemDataForOverview {
  translatedDimensionValue: string;
  breakdownByDimensionValue: string;
  errorCount: number;
  operationCount: number;
  traceRefs: ErrorInsightsBreakdownItemFragment['traceRefs'];
}

interface ForOverviewProps {
  dimensionName: string;
  groupByDimension: GroupOption;
  groupByDimensionValue: string;
  breakdownByDimension: BreakdownOption;
  data: ErrorBreakdownItemDataForOverview[];
}

export function ErrorBreakdownForOverview({
  dimensionName,
  groupByDimension,
  groupByDimensionValue,
  breakdownByDimension,
  data,
}: ForOverviewProps) {
  const location = useLocation();
  const history = useHistory();
  const trackCustomEvent = useTrackCustomEvent();
  const handleDrilldownClick = useCallback(() => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'error_breakdown_drilldown_click',
    });
  }, [trackCustomEvent]);

  return (
    <TableContainer>
      <Table layout="fixed" isInteractive size="condensed">
        <Thead>
          <Tr>
            <Th>{dimensionName}</Th>
            <Th>
              <SampleMessageHeader />
            </Th>
            <Th className="w-[15%] max-2xl:pl-0" isNumeric>
              Operations
            </Th>
            <Th className="w-[10%] max-2xl:pl-0" isNumeric>
              Errors
            </Th>
            <Th className="w-[180px] pl-0 max-2xl:hidden" />
          </Tr>
        </Thead>
        <Tbody>
          {data.map((d) => (
            <Tr
              key={d.breakdownByDimensionValue}
              className="group cursor-pointer hover:bg-info"
              tabIndex={0}
              onClick={() => {
                handleDrilldownClick();
                history.push(
                  insightsRouteConfig.locationFrom({
                    location,
                    patch: {
                      errorsDrilldown: 'OPERATION',
                      [getRouteParamForGroupOption(groupByDimension)]:
                        groupByDimensionValue,
                      [getRouteParamForBreakdownOption(breakdownByDimension)]:
                        d.breakdownByDimensionValue,
                    },
                  }),
                );
              }}
            >
              <Td>
                <div className="flex items-center">
                  <Text
                    size="sm"
                    variant="code"
                    className="mr-1 overflow-hidden truncate rounded border border-primary bg-secondary px-2 py-0.5"
                  >
                    <GroupIcon
                      groupByDimension={breakdownByDimension}
                      dimensionValue={d.breakdownByDimensionValue}
                    />
                    <span
                      className="ml-2 align-middle leading-tight"
                      title={d.translatedDimensionValue}
                    >
                      {d.translatedDimensionValue}
                    </span>
                  </Text>
                  <GroupRightIcon
                    groupByDimension={breakdownByDimension}
                    dimensionValue={d.breakdownByDimensionValue}
                  />
                </div>
              </Td>
              <Td>
                <SampleMessage traceRefs={d.traceRefs} />
              </Td>
              <Td isNumeric className="pl-1">
                {d.operationCount > 0 ? (
                  <span className="font-mono">
                    {format.number(d.operationCount, '0[.]0a')}
                  </span>
                ) : (
                  <span className="inline-flex items-center">
                    <Tooltip label="Operations with these errors were reported without the original operation context.">
                      <span className="mr-1 italic text-disabled">
                        unavailable
                      </span>
                    </Tooltip>
                  </span>
                )}
              </Td>
              <Td isNumeric className="pl-1">
                <span className="font-mono">
                  {format.number(d.errorCount, '0[.]0a')}
                </span>
              </Td>
              <Td className="pl-0 max-2xl:hidden">
                {d.operationCount > 0 ? (
                  <div className="invisible flex items-center justify-end whitespace-nowrap group-hover:visible">
                    <Text variant="bodySb">View operations</Text>
                    <IconArrowRight className="ml-2 mt-0.75" />
                  </div>
                ) : (
                  ' '
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function breakdownValueForListItem(
  item: ErrorInsightsBreakdownItemFragment,
  breakdownByDimension: BreakdownOption,
) {
  switch (breakdownByDimension) {
    case ErrorInsightsListGroupByColumn.SERVICE:
      return item.service ?? '';
    case ErrorInsightsListGroupByColumn.CODE:
      return item.code ?? '';
    case ErrorInsightsListGroupByColumn.PATH:
      return item.path ?? '';
    default:
      assertUnreachable(breakdownByDimension);
  }
}

interface WithDataProps {
  groupByDimension: GroupOption;
  groupByDimensionValue: string;
  groupColor: string;
  breakdownByDimension: BreakdownOption;
  inView: boolean;
  pageSize?: number;
}

const defaultPageSize = 50;
const defaultTraceRefsLimit = 3;

export function ErrorBreakdownWithData({
  groupByDimension,
  groupByDimensionValue,
  groupColor,
  breakdownByDimension,
  inView,
  pageSize = defaultPageSize,
}: WithDataProps) {
  const { routeParams } = useOperationInsightsFilter();
  const timeRange = routeParams.range ?? 'lastDay';
  const { timeFrom, timeTo, pollInterval } = date.getDateBounds(
    timeRange,
    {
      from: routeParams.from,
      to: routeParams.to,
    },
    undefined,
    routeParams.fastModeRange,
  );
  const [fetchFirstN, setFetchFirstN] = useState(pageSize);
  const handleLoadMore = useCallback(() => {
    setFetchFirstN((previousValue) => previousValue + pageSize);
  }, [pageSize]);
  const graphRef = useGraphRef();
  const isInBackground = useIsAppInBackgroundContext();
  const isOverview = !routeParams.query;

  const { data, loading } = useQuery<ErrorInsightsBreakdownQuery>(
    errorInsightsBreakdownQuery,
    {
      variables: {
        graphId: graphRef?.graphId,
        variant: graphRef?.graphVariant,
        first: fetchFirstN,
        filter: {
          ...(groupByDimension === ErrorInsightsListGroupByColumn.SERVICE
            ? { service: groupByDimensionValue }
            : {}),
          ...(groupByDimension === ErrorInsightsListGroupByColumn.CODE
            ? { code: groupByDimensionValue }
            : {}),
          ...buildErrorFiltersForListQuery(routeParams),
        },
        groupBy: [breakdownByDimension],
        from: timeFrom,
        to: timeTo,
        isOverview,
        traceRefsLimit: isOverview ? 1 : defaultTraceRefsLimit,
      },
      skip: !graphRef || !inView,
      pollInterval,
      skipPollAttempt: useCallback(
        () => isInBackground || !inView,
        [inView, isInBackground],
      ),
    },
  );

  const { translateToDisplayName } = useDisplayNameTranslator();

  const nodes =
    data?.graph?.variant?.errorInsightsList.edges
      ?.map((e) => e.node)
      .filter(notEmpty) ?? [];
  const totalCount = data?.graph?.variant?.errorInsightsList.totalCount ?? 0;

  return loading || !inView ? (
    <ErrorBreakdownLoading />
  ) : (
    <>
      {isOverview ? (
        <ErrorBreakdownForOverview
          dimensionName={capitalize(breakdownByDimension)}
          groupByDimension={groupByDimension}
          groupByDimensionValue={groupByDimensionValue}
          breakdownByDimension={breakdownByDimension}
          data={nodes.map((item) => ({
            translatedDimensionValue: translateToDisplayName(
              breakdownValueForListItem(item, breakdownByDimension),
              breakdownByDimension,
            ),
            breakdownByDimensionValue: breakdownValueForListItem(
              item,
              breakdownByDimension,
            ),
            errorCount: item.errorCount ?? 0,
            operationCount: item.operationCount ?? 0,
            traceRefs: item.traceRefs,
          }))}
        />
      ) : (
        <ErrorBreakdown
          dimensionName={capitalize(breakdownByDimension)}
          groupColor={groupColor}
          breakdownByDimension={breakdownByDimension}
          data={nodes.map((item) => ({
            translatedDimensionValue: translateToDisplayName(
              breakdownValueForListItem(item, breakdownByDimension),
              breakdownByDimension,
            ),
            dimensionValue: breakdownValueForListItem(
              item,
              breakdownByDimension,
            ),
            errorCount: item.errorCount ?? 0,
            traceRefs: item.traceRefs,
          }))}
        />
      )}
      {totalCount > fetchFirstN ? (
        <div className="flex justify-center border-t border-primary">
          <span className="p-1">
            <span className="mr-2 text-sm">
              <em>1 - {fetchFirstN}</em> of {totalCount}
            </span>
            <Button size="xs" variant="buttonLink" onClick={handleLoadMore}>
              Load More
            </Button>
          </span>
        </div>
      ) : null}
    </>
  );
}
